import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BayclubsAlertService } from '@bayclubs/alert';
import {
    Api10RefundsPostRequestParams,
    Api10TransactionsPostRequestParams,
    InitiateRefundItemModel,
    InitiateTransactionItemModel,
    InitiateTransactionRequest,
    TransactionSourceExternalTypeModel,
    TransactionSourceModel,
    TransactionTypeModel,
} from '@bayclubs/payments-processor-office-api';
import { map, repeat, skipWhile, take, tap } from 'rxjs';
import { campsenum } from '../models/enums.generated';
import { camps } from '../models/models.generated';
import { SellCampApiService } from './api/sell-camp-api.service';

@Injectable({ providedIn: 'root' })
export class PaymentProcessorIntegrationService {
    private alert = inject(BayclubsAlertService);
    private router = inject(Router);
    private sellCampService = inject(SellCampApiService);

    public mapToPaymentProcessorTransaction(response: camps.CreateCampPurchaseResponse): Api10TransactionsPostRequestParams {
        const items = [];

        items.push({
            baseUnitPrice: response.summary.finalNetPriceIncludingDeductions,
            discountAmount: 0,
            quantity: 1,
            taxAmount: response.summary.sessionsTax,
            itemName: `session_${response.summary.sessionName}_${response.summary.sessionVariantName}_${response.summary.childName}`,
            itemCode: `${response.summary.sessionId}`,
            notes: '',
            taxInPercent: response.summary.taxRate > 0 ? response.summary.taxRate : 0,
        } as InitiateTransactionItemModel);

        response.summary?.options.forEach((option) => {
            items.push({
                baseUnitPrice: option.finalNetToBePaid,
                discountAmount: 0,
                quantity: 1,
                taxAmount: option.tax,
                itemName: `option_${option.name}_session_${response.summary.sessionName}`,
                itemCode: `${option.id}`,
                notes: '',
                taxInPercent: option.taxRate,
            } as InitiateTransactionItemModel);
        });

        const request = {
            initiateTransactionRequest: {
                type: TransactionTypeModel.Sale,
                source: TransactionSourceModel.Camps,
                externalType: TransactionSourceExternalTypeModel.CampSale,
                items: items,
                personId: response.personId,
                externalId: response.purchaseId,
                ticketNumber: response.ticketNumber,
                clubNumber: response.clubNumber,
                creditCardFeeTaxInPercent: response.paymentMethodFeeTaxRate,
                registerCode: response.summary.registerCode,
            } as InitiateTransactionRequest,
        } as Api10TransactionsPostRequestParams;

        return request;
    }

    public mapToPaymentProcessorRefund(summary: camps.OfficeCartSummaryInfo): Api10RefundsPostRequestParams {
        const sessionItems = summary.sessions
            .filter((s) => s.isRemoved)
            .map((session) => {
                return {
                    quantity: 1,
                    itemCode: session.purchasedCampSessionId,
                    itemName: `session_${session.sessionName}_${session.daysVariant}_${session.childFullName}`,
                    baseUnitPrice: 0,
                    discountAmount: 0,
                    taxAmount: 0,
                    notes: null,
                    taxInPercent: 0,
                } as InitiateRefundItemModel;
            });

        const optionsItems = summary.sessions.flatMap((session) =>
            session.removedOptions.map(
                (option: any) =>
                    ({
                        quantity: 1,
                        itemCode: option.id,
                        itemName: `option_${option.name}_session_${session.sessionName}`,
                        baseUnitPrice: 0,
                        discountAmount: 0,
                        taxAmount: 0,
                        notes: null,
                        taxInPercent: 0,
                    } as InitiateRefundItemModel)
            )
        );

        const params: Api10RefundsPostRequestParams = {
            initiateRefundRequest: {
                source: TransactionSourceModel.Camps,
                payer: {
                    email: summary.payer?.email,
                    memberId: summary.payer?.memberId,
                    name: summary.payer?.name,
                    payerId: summary.payer?.personId,
                    phone: summary.payer?.phoneNumber,
                },
                transactionExternalId: summary.firstPurchaseId,
                refundExternalId: summary.purchaseId,
                ticketNumber: summary.ticketNumber,
                registerCode: summary.registerCode,
                clubNumber: summary.clubNumber,

                totalRefundAmount: summary.summary.valueToBePaidOrReturned,
                refundSurcharge: summary.summary.paymentMethodValueChange,
                refundSurchargeTax: summary.summary.paymentMethodFeeTaxChange,
                refundAmountTax: summary.summary.taxValueToBePaidOrReturned,
                refundAmountWithoutTax: summary.summary.netValueToBePaidOrReturned,
                creditCardFeeTaxInPercent: undefined,

                items: [...sessionItems, ...optionsItems],
            },
        };

        return params;
    }

    public mapToPaymentProcessorFullRefund(summary: camps.CampPurchaseDetails, refundData: camps.InitRefundResponse): Api10RefundsPostRequestParams {
        const sessionItems = summary.sessions.map((session) => {
            return {
                quantity: 1,
                itemCode: session.id,
                itemName: `session_${session.sessionName}_${session.daysVariant}_${session.childFullName}`,
                baseUnitPrice: session.cost.basePrice,
                discountAmount: 0,
                taxAmount: session.cost.taxAmount,
                notes: null,
                taxInPercent: session.cost.taxRate > 0 ? session.cost.taxRate : null,
            } as InitiateRefundItemModel;
        });

        const optionsItems = summary.sessions.flatMap((session) =>
            session.selectedOptions.map(
                (option: camps.SelectedOptionInfo) =>
                    ({
                        quantity: 1,
                        itemCode: option.id,
                        itemName: `option_${option.name}_session_${session.sessionName}`,
                        baseUnitPrice: option.pricePerSession,
                        discountAmount: 0,
                        taxAmount: option.tax,
                        notes: null,
                        taxInPercent: option.taxRate > 0 ? option.taxRate : null,
                    } as InitiateRefundItemModel)
            )
        );

        const params: Api10RefundsPostRequestParams = {
            initiateRefundRequest: {
                source: TransactionSourceModel.Camps,
                payer: {
                    email: summary.payer?.email,
                    memberId: summary.payer?.memberId,
                    name: summary.payer?.name,
                    payerId: summary.payer?.personId,
                    phone: summary.payer?.phoneNumber,
                },
                transactionExternalId: summary.purchaseId,
                refundExternalId: refundData.refundId,
                ticketNumber: summary.ticketNumber,
                registerCode: summary.registerCode,
                clubNumber: summary.clubNumber,

                creditCardFeeTaxInPercent: undefined,

                totalRefundAmount: refundData.totalRefundAmount,

                refundAmountWithoutTax: refundData.refundAmountWithoutTax,
                refundAmountTax: refundData.refundAmountTax,
                refundSurcharge: refundData.refundSurcharge,
                refundSurchargeTax: refundData.refundSurchargeTax,

                items: [...sessionItems, ...optionsItems],
            },
        };

        return params;
    }

    checkTransactionStatus(purchaseId: camps.Guid) {
        return this.sellCampService.getPurchaseStatus(purchaseId).pipe(
            repeat({ delay: 2000 }),
            tap((response) => console.log(response)),
            skipWhile(
                (response) =>
                    response.value.status !== campsenum.CampPurchaseStatus.Finalized &&
                    response.value.status !== campsenum.CampPurchaseStatus.Abandoned &&
                    response.value.status !== campsenum.CampPurchaseStatus.PaymentNotValid
            ),
            take(1),
            map((response) => {
                if (response.value.status === campsenum.CampPurchaseStatus.Finalized) {
                    this.router.navigate(['purchases/' + purchaseId + '/details']);
                    return undefined;
                } else if (response.value.status === campsenum.CampPurchaseStatus.Abandoned) {
                    this.alert.showInfo('Payment abandoned.');
                    return false;
                } else if (response.value.status === campsenum.CampPurchaseStatus.PaymentNotValid) {
                    this.alert.showError('Received payment was not valid, manual intervention required');
                    this.router.navigate(['purchases/' + purchaseId + '/details']);
                    return false;
                } else {
                    console.log('wait for status');
                    return undefined;
                }
            })
        );
    }
}
